import { type ReactNode, useId, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { TextCursorInput } from 'lucide-react';
import { Dialog, DialogApply } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { errorMessage } from '../../../lib/error-message';
import { PreviewAction } from '../../my-files/preview/preview-frame';
import { documentApi, documentKeys } from '../api';

/** Same ceiling as `UpdateDocumentInput.title` in `@apm/contracts`. */
const MAX_TITLE = 300;

/**
 * Nút "Đổi tên" tài liệu dự án — APM-DREN. Đặt ở chỗ người xem có
 * `document:write` — cùng cửa với `PATCH /documents/:id`.
 *
 * Chỉ đổi **tiêu đề** (tên hiển thị trong dự án): tệp của các phiên bản giữ
 * nguyên tên và object key, nên không có đuôi tệp nào để giữ ở đây — tiêu đề
 * vốn đã không mang đuôi (`documentTitleOf`). Hộp nhỏ kiểu `RenameFileDialog`
 * của trang Tệp: Enter lưu, Esc / ✕ huỷ.
 */
export function RenameDocumentButton({
  document,
  projectId,
  inPreview = false,
}: {
  readonly document: { readonly id: string; readonly title: string };
  readonly projectId: string;
  /** Đặt trên thanh nút của khung xem trước — cùng kiểu với các nút cạnh nó. */
  readonly inPreview?: boolean;
}): ReactNode {
  const { t } = useTranslation();
  const [asking, setAsking] = useState(false);

  return (
    <>
      {inPreview ? (
        <PreviewAction
          label={t('documents.rename')}
          icon={<TextCursorInput aria-hidden />}
          onClick={() => setAsking(true)}
        />
      ) : (
        <button
          type="button"
          onClick={() => setAsking(true)}
          aria-label={t('documents.rename')}
          title={t('documents.rename')}
          className="glass-hover rounded-full p-1 text-muted-foreground hover:text-foreground"
        >
          <TextCursorInput className="h-4 w-4" aria-hidden />
        </button>
      )}

      {asking ? (
        <RenameDocumentDialog
          document={document}
          projectId={projectId}
          onClose={() => setAsking(false)}
        />
      ) : null}
    </>
  );
}

export function RenameDocumentDialog({
  document,
  projectId,
  onClose,
}: {
  readonly document: { readonly id: string; readonly title: string };
  readonly projectId: string;
  readonly onClose: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const inputId = useId();
  const formId = useId();
  const [draft, setDraft] = useState(document.title);

  const title = draft.trim();
  const problem =
    title === ''
      ? t('documents.renameEmpty')
      : title.length > MAX_TITLE
        ? t('documents.renameTooLong', { max: MAX_TITLE })
        : null;

  const rename = useMutation({
    mutationFn: () => documentApi.update(document.id, { title }),
    onSuccess: (saved) => {
      queryClient.setQueryData(documentKeys.detail(saved.id), saved);
      // Every list filter of the project (root key), and the "Changes" tab line.
      void queryClient.invalidateQueries({ queryKey: ['document'] });
      void queryClient.invalidateQueries({ queryKey: ['project-activity', projectId] });
      toast.success(t('documents.renamed', { title: saved.title }));
      onClose();
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const heading = t('documents.renameTitle', { title: document.title });
  return (
    <Dialog label={heading} onClose={onClose}>
      <form
        id={formId}
        className="flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (rename.isPending || problem !== null) return;
          if (title === document.title) onClose();
          else rename.mutate();
        }}
      >
        <h2 className="text-base font-medium">{heading}</h2>
        <label htmlFor={inputId} className="text-sm">
          {t('documents.renameLabel')}
        </label>
        <Input
          id={inputId}
          value={draft}
          autoFocus
          onFocus={(event) => event.currentTarget.select()}
          onChange={(event) => setDraft(event.target.value)}
          aria-invalid={problem !== null || undefined}
        />
        {problem ? (
          <p role="alert" className="text-xs text-destructive">
            {problem}
          </p>
        ) : (
          <p className="text-xs text-muted-foreground">{t('documents.renameHint')}</p>
        )}
        <DialogApply
          label={t('documents.rename')}
          disabled={problem !== null}
          pending={rename.isPending}
          form={formId}
        />
      </form>
    </Dialog>
  );
}
